import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  InputAdornment,
  Snackbar,
  Typography,
  Box,
  Paper,
  Fade,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Divider,
  Stepper,
  Step,
  StepLabel,
  Avatar,
  Card,
  CardContent,
} from '@mui/material';
import {
  AttachMoney,
  Email,
  Person,
  Notes,
  Receipt,
  Close,
  Refresh,
  Send,
  Calculate,
  Timeline,
  ArrowBack,
  ArrowForward,
  CheckCircle,
  Description,
} from '@mui/icons-material';
import { useCreatorAuth } from '../../../../Hooks/creator-use-auth';
import API from '../../../../API';

function CreatorInvoiceDialog({ open, onClose, onInvoiceCreated }) {
  const { creatorToken } = useCreatorAuth();
  const username = creatorToken?.creator_user?.username;

  // Wizard state
  const [currentStep, setCurrentStep] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    clientName: '',
    clientEmail: '',
    amount: '',
    notes: '',
    poNumber: '',
  });

  // Calculation state
  const [calculations, setCalculations] = useState({
    interestRate: 10,
    blitzFee: 0,
    netToReceive: 0,
    processingFee: 0,
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [userFound, setUserFound] = useState(null);
  const [errors, setErrors] = useState({});
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Steps configuration
  const steps = [
    { label: 'Client', icon: <Email /> },
    { label: 'Details', icon: <Description /> },
    { label: 'Review', icon: <CheckCircle /> },
  ];

  // Generate PO number
  const generatePONumber = () => {
    const timestamp = Date.now().toString().slice(-6);
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `CR-${timestamp}-${random}`;
  };

  // Initialize dialog
  useEffect(() => {
    if (open) {
      setFormData(prev => ({
        ...prev,
        poNumber: generatePONumber(),
      }));
      setUserFound(null);
      setErrors({});
      setCurrentStep(0);
    }
  }, [open]);

  // Calculate fees and net amount
  useEffect(() => {
    const amount = parseFloat(formData.amount) || 0;
    const rate = calculations.interestRate;
    const blitzFee = (amount * rate) / 100;
    const processingFee = amount * 0.029 + 0.30; // Standard processing fee
    const netToReceive = amount - blitzFee - processingFee;

    setCalculations(prev => ({
      ...prev,
      blitzFee,
      processingFee,
      netToReceive: Math.max(0, netToReceive),
    }));
  }, [formData.amount, calculations.interestRate]);

  // Navigation
  const goToStep = (step) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentStep(step);
      setIsTransitioning(false);
    }, 150);
  };

  const nextStep = () => currentStep < steps.length - 1 && goToStep(currentStep + 1);
  const prevStep = () => currentStep > 0 && goToStep(currentStep - 1);

  // Handle form field changes
  const handleFieldChange = (field, value) => {
    const processedValue = field === 'clientEmail' ? value.toLowerCase() : value;
    setFormData(prev => ({ ...prev, [field]: processedValue }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Check if email exists in system
  const handleCheckEmail = async () => {
    if (!formData.clientEmail || !formData.clientEmail.includes('@')) return;
    
    setEmailLoading(true);
    try {
      const response = await API.creatorConnect.fetchUserInfo(formData.clientEmail);
      if (response?.found) {
        const userData = response.user;
        setFormData(prev => ({
          ...prev,
          clientName: `${userData.first_name} ${userData.last_name}`,
        }));
        setCalculations(prev => ({
          ...prev,
          interestRate: userData.company_interest_rate ?? 10,
        }));
        setUserFound(true);
        showSnackbar('Client found! Company rate applied.', 'success');
      } else {
        setCalculations(prev => ({ ...prev, interestRate: 10 }));
        setUserFound(false);
        showSnackbar('New client - default rate applied.', 'info');
      }
    } catch (err) {
      console.error('Error fetching user info:', err);
      showSnackbar('Error checking client information', 'error');
      setUserFound(false);
    } finally {
      setEmailLoading(false);
    }
  };

  // Validation per step
  const canProceedFromStep = (step) => {
    switch (step) {
      case 0:
        return formData.clientEmail && formData.clientName && !emailLoading;
      case 1:
        return formData.amount && parseFloat(formData.amount) > 0;
      case 2:
        return true;
      default:
        return false;
    }
  };

  // Handle step navigation
  const handleNextStep = () => {
    if (currentStep === steps.length - 1) {
      handleCreateInvoice();
    } else if (canProceedFromStep(currentStep)) {
      nextStep();
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.clientEmail) newErrors.clientEmail = 'Client email is required';
    else if (!formData.clientEmail.includes('@')) newErrors.clientEmail = 'Invalid email format';
    
    if (!formData.clientName.trim()) newErrors.clientName = 'Client name is required';
    
    if (!formData.amount) newErrors.amount = 'Amount is required';
    else if (parseFloat(formData.amount) <= 0) newErrors.amount = 'Amount must be greater than 0';
    else if (parseFloat(formData.amount) > 100000) newErrors.amount = 'Amount too large';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Create invoice
  const handleCreateInvoice = async () => {
    if (!validateForm()) {
      showSnackbar('Please fix the errors above', 'error');
      return;
    }

    if (!username) {
      showSnackbar('Authentication required', 'error');
      return;
    }

    setLoading(true);
    try {
      const amount = parseFloat(formData.amount);
      
      const invoiceData = {
        creator_id: username,
        po_number: formData.poNumber,
        amount_due: amount,
        status: 'pending',
        invoice_type: 'creator',
        source_type: 'creator',
        blitzpay: true,
        interest_rate: calculations.interestRate,
        unique_code: formData.poNumber,
        client_name: formData.clientName.trim(),
        client_email: formData.clientEmail.trim(),
        notes: formData.notes.trim() || null,
        payout_date: new Date().toISOString(),
      };

      const response = await API.creatorConnect.postInvoice(invoiceData);
      
      showSnackbar('Invoice created successfully!', 'success');
      
      // Reset form
      setFormData({
        clientName: '',
        clientEmail: '',
        amount: '',
        notes: '',
        poNumber: generatePONumber(),
      });
      setCalculations(prev => ({ ...prev, interestRate: 10 }));
      setUserFound(null);
      setCurrentStep(0);

      // Notify parent component
      if (response?.invoice) {
        onInvoiceCreated(response.invoice);
      } else {
        onInvoiceCreated({
          id: response.invoice_id,
          po_number: formData.poNumber,
          amount_due: amount,
          client_name: formData.clientName,
          status: 'pending',
        });
      }
      
      onClose();
    } catch (error) {
      console.error('Error creating invoice:', error);
      showSnackbar(error.message || 'Failed to create invoice', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Step content renderers
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <Fade in={!isTransitioning} timeout={300}>
            <Box>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 80, height: 80, mx: 'auto', mb: 2 }}>
                 <Email sx={{ fontSize: 40 }} />
                </Avatar>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                  Client Information
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Who are you invoicing?
                </Typography>
              </Box>

              <Stack spacing={3}>
                <TextField
                  fullWidth
                  label="Client Email Address"
                  value={formData.clientEmail}
                  onChange={(e) => handleFieldChange('clientEmail', e.target.value)}
                  onBlur={handleCheckEmail}
                  error={!!errors.clientEmail}
                  helperText={errors.clientEmail}
                  autoFocus
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email color={errors.clientEmail ? 'error' : 'action'} />
                      </InputAdornment>
                    ),
                    endAdornment: emailLoading && (
                      <InputAdornment position="end">
                        <CircularProgress size={20} />
                      </InputAdornment>
                    ),
                    sx: { fontSize: '1.05rem', py: 1.5 }
                  }}
                />

                {userFound !== null && (
                  <Fade in={true}>
                    <Alert 
                      severity={userFound ? 'success' : 'info'}
                      sx={{ borderRadius: 2 }}
                      icon={userFound ? <CheckCircle /> : <Person />}
                    >
                      {userFound 
                        ? `Existing client found - ${calculations.interestRate}% company rate applied`
                        : 'New client - 10% default rate will be used'
                      }
                    </Alert>
                  </Fade>
                )}

                <TextField
                  fullWidth
                  label="Client Full Name"
                  value={formData.clientName}
                  onChange={(e) => handleFieldChange('clientName', e.target.value)}
                  error={!!errors.clientName}
                  helperText={errors.clientName || 'This will appear on the invoice'}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person color={errors.clientName ? 'error' : 'action'} />
                      </InputAdornment>
                    ),
                    sx: { fontSize: '1.05rem', py: 1.5 }
                  }}
                />
              </Stack>
            </Box>
          </Fade>
        );

      case 1:
        return (
          <Fade in={!isTransitioning} timeout={300}>
            <Box>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Avatar sx={{ bgcolor: 'success.main', width: 80, height: 80, mx: 'auto', mb: 2 }}>
                  <AttachMoney sx={{ fontSize: 40 }} />
                </Avatar>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                  Invoice Details
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Set the amount and add any notes
                </Typography>
              </Box>

              <Stack spacing={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="Invoice Amount"
                  value={formData.amount}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || (Number(value) >= 0 && Number(value) <= 100000)) {
                      handleFieldChange('amount', value);
                    }
                  }}
                  error={!!errors.amount}
                  helperText={errors.amount}
                  autoFocus
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AttachMoney color={errors.amount ? 'error' : 'action'} />
                      </InputAdornment>
                    ),
                    sx: { fontSize: '1.25rem', py: 1.5 }
                  }}
                />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                  <Receipt color="action" />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      PO Number
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {formData.poNumber}
                    </Typography>
                  </Box>
                  <Tooltip title="Generate new PO number">
                    <IconButton 
                      size="small" 
                      onClick={() => handleFieldChange('poNumber', generatePONumber())}
                    >
                      <Refresh />
                    </IconButton>
                  </Tooltip>
                </Box>

                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Notes (Optional)"
                  value={formData.notes}
                  onChange={(e) => handleFieldChange('notes', e.target.value)}
                  placeholder="Add any details about this invoice..."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                        <Notes color="action" />
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Live calculation preview */}
                {formData.amount && (
                  <Card variant="outlined" sx={{ borderRadius: 3 }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
                        Payment Preview
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography>Invoice Amount:</Typography>
                        <Typography sx={{ fontWeight: 600 }}>
                          ${parseFloat(formData.amount || 0).toFixed(2)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography>Processing Fee ({calculations.interestRate}%):</Typography>
                        <Typography color="error.main">
                          -${calculations.blitzFee.toFixed(2)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: 1, borderColor: 'divider' }}>
                        <Typography sx={{ fontWeight: 700, color: 'success.main' }}>You'll Receive:</Typography>
                        <Typography sx={{ fontWeight: 700, color: 'success.main', fontSize: '1.1rem' }}>
                          ${calculations.netToReceive.toFixed(2)}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                )}
              </Stack>
            </Box>
          </Fade>
        );

      case 2:
        return (
          <Fade in={!isTransitioning} timeout={300}>
            <Box>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Avatar sx={{ bgcolor: 'info.main', width: 80, height: 80, mx: 'auto', mb: 2 }}>
                  <CheckCircle sx={{ fontSize: 40 }} />
                </Avatar>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                  Review & Confirm
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Double-check your invoice details
                </Typography>
              </Box>

              <Stack spacing={3}>
                {/* Client Summary */}
                <Card variant="outlined" sx={{ borderRadius: 3 }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                      <Person sx={{ mr: 1 }} />
                      Client Information
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography color="text.secondary">Name:</Typography>
                      <Typography sx={{ fontWeight: 600 }}>{formData.clientName}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography color="text.secondary">Email:</Typography>
                      <Typography>{formData.clientEmail}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography color="text.secondary">Status:</Typography>
                      <Chip 
                        label={userFound ? 'Existing Client' : 'New Client'} 
                        size="small"
                        color={userFound ? 'success' : 'primary'}
                        variant="outlined"
                      />
                    </Box>
                  </CardContent>
                </Card>

                {/* Invoice Summary */}
                <Card variant="outlined" sx={{ borderRadius: 3 }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                      <Receipt sx={{ mr: 1 }} />
                      Invoice Details
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography color="text.secondary">PO Number:</Typography>
                      <Typography sx={{ fontWeight: 600 }}>{formData.poNumber}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography color="text.secondary">Amount:</Typography>
                      <Typography sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                        ${parseFloat(formData.amount || 0).toFixed(2)}
                      </Typography>
                    </Box>
                    {formData.notes && (
                      <Box sx={{ mt: 2 }}>
                        <Typography color="text.secondary" sx={{ mb: 1 }}>Notes:</Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            p: 2, 
                            bgcolor: 'grey.50', 
                            borderRadius: 1, 
                            fontStyle: 'italic' 
                          }}
                        >
                          {formData.notes}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>

                {/* Final Calculation */}
                <Card 
                  variant="outlined" 
                  sx={{ 
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                      <Calculate sx={{ mr: 1 }} />
                      Final Payment Breakdown
                    </Typography>
                    
                    <Stack spacing={1.5}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography>Invoice Amount:</Typography>
                        <Typography sx={{ fontWeight: 600 }}>
                          ${parseFloat(formData.amount || 0).toFixed(2)}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography>Interest Rate:</Typography>
                        <Chip 
                          label={`${calculations.interestRate}%`} 
                          size="small" 
                          color="primary"
                          variant="outlined"
                        />
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography color="error.main">Blitz Fee:</Typography>
                        <Typography color="error.main">
                          -${calculations.blitzFee.toFixed(2)}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography color="error.main">Processing Fee:</Typography>
                        <Typography color="error.main">
                          -${calculations.processingFee.toFixed(2)}
                        </Typography>
                      </Box>
                      
                      <Divider />
                      
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="h6" color="success.main">
                          You'll Receive:
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>
                          ${calculations.netToReceive.toFixed(2)}
                        </Typography>
                      </Box>
                      
                      <Typography variant="caption" color="text.secondary" textAlign="center">
                        Funds typically available within 24 hours of payment
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>
              </Stack>
            </Box>
          </Fade>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose} 
        fullWidth 
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
            minHeight: '700px',
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            pb: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Receipt />
            <Typography variant="h6" component="div">
              Create Invoice
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <Box sx={{ px: 3 }}>
          {/* Stepper */}
          <Stepper activeStep={currentStep} sx={{ mb: 3 }}>
            {steps.map((stepConfig, index) => (
              <Step key={stepConfig.label}>
                <StepLabel 
                  StepIconComponent={({ active, completed }) => (
                    <Avatar 
                      sx={{ 
                        width: 32, 
                        height: 32,
                        bgcolor: completed ? 'success.main' : active ? 'primary.main' : 'grey.300',
                        color: completed || active ? 'white' : 'grey.600'
                      }}
                    >
                      {completed ? <CheckCircle sx={{ fontSize: 20 }} /> : stepConfig.icon}
                    </Avatar>
                  )}
                >
                  <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                    {stepConfig.label}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        <DialogContent sx={{ px: 3, minHeight: 400 }}>
          {renderStepContent()}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 1, justifyContent: 'space-between' }}>
          <Button 
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={prevStep}
            disabled={currentStep === 0 || loading}
            sx={{ minWidth: 100 }}
          >
            Back
          </Button>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button 
              onClick={onClose} 
              variant="outlined"
              sx={{ minWidth: 100 }}
            >
              Cancel
            </Button>
            
            {currentStep === steps.length - 1 ? (
              <Button 
                variant="contained" 
                onClick={handleNextStep}
                disabled={loading || !canProceedFromStep(currentStep)}
                startIcon={loading ? <CircularProgress size={20} /> : <Send />}
                sx={{ 
                  minWidth: 160,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                }}
              >
                {loading ? 'Creating...' : 'Create Invoice'}
              </Button>
            ) : (
              <Button
                variant="contained"
                endIcon={<ArrowForward />}
                onClick={handleNextStep}
                disabled={!canProceedFromStep(currentStep) || loading || emailLoading}
                sx={{ minWidth: 100 }}
              >
                Next
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default CreatorInvoiceDialog;